<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.TRANSFER_ORDERS.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.TRANSFER_ORDERS.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#main">{{ document.code || $t("STOCK.TRANSFER_ORDERS.NEW") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn v-if="canConfirm" class="mr-2 primary" small @click="confirmOrder">
            {{ $t("STOCK.TRANSFER_ORDERS.CONFIRM") }}
          </v-btn>
          <v-btn v-if="canShip" class="mr-2 success" small @click="shipOrder">
            {{ $t("STOCK.TRANSFER_ORDERS.SHIP") }}
          </v-btn>
          <v-btn v-if="canReceive" class="mr-2 success" small @click="receiveOrder">
            {{ $t("STOCK.TRANSFER_ORDERS.RECEIVE") }}
          </v-btn>
          <v-btn v-if="canResolveVariance" class="mr-2 warning" small @click="resolveVariance">
            {{ $t("STOCK.TRANSFER_ORDERS.RESOLVE_VARIANCE") }}
          </v-btn>
          <v-btn v-if="canCancel" class="mr-2 error" small @click="cancelOrder">
            {{ $t("STOCK.TRANSFER_ORDERS.CANCEL") }}
          </v-btn>
          <v-btn v-if="canDiscard" class="mr-2 error" small @click="discardOrder">
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn v-if="canSave" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="main">
          <v-card flat>
            <v-card-text>
              <v-alert
                v-if="isReadOnlyDocument"
                type="warning"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.TRANSFER_ORDERS.READ_ONLY_DOCUMENT") }}
              </v-alert>

              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.source_warehouse_id"
                    :items="sourceWarehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.TRANSFER_ORDERS.SOURCE_WAREHOUSE') + '*'"
                    :hint="$t('STOCK.TRANSFER_ORDERS.SOURCE_WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.source_warehouse_id || []"
                    @change="clearHeaderValidationError('source_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.destination_warehouse_id"
                    :items="destinationWarehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.TRANSFER_ORDERS.DESTINATION_WAREHOUSE') + '*'"
                    :hint="$t('STOCK.TRANSFER_ORDERS.DESTINATION_WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.destination_warehouse_id || []"
                    @change="clearHeaderValidationError('destination_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="document.requested_date"
                    type="date"
                    :label="$t('STOCK.TRANSFER_ORDERS.REQUESTED_DATE') + '*'"
                    :disabled="!canSave"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="document.notes"
                    :label="$t('STOCK.TRANSFER_ORDERS.NOTES')"
                    :disabled="!canSave"
                    rows="2"
                    auto-grow
                    :error-messages="validationErrors.notes || []"
                  ></v-textarea>
                </v-col>
                <v-col
                  v-if="document.shipped_action || document.received_action"
                  cols="12"
                  md="6"
                  class="d-flex flex-column justify-center"
                >
                  <v-alert
                    v-if="document.shipped_action"
                    type="info"
                    dense
                    text
                    class="mb-2"
                  >
                    {{ $t("STOCK.TRANSFER_ORDERS.SHIPPED_ACTION") }}:
                    <router-link
                      :to="{
                        name: 'stock-transfers-view',
                        params: { id: document.shipped_action.id },
                      }"
                    >
                      {{ document.shipped_action.code }}
                    </router-link>
                  </v-alert>
                  <v-alert
                    v-if="document.received_action"
                    type="info"
                    dense
                    text
                    class="mb-0"
                  >
                    {{ $t("STOCK.TRANSFER_ORDERS.RECEIVED_ACTION") }}:
                    <router-link
                      :to="{
                        name: 'stock-transfers-view',
                        params: { id: document.received_action.id },
                      }"
                    >
                      {{ document.received_action.code }}
                    </router-link>
                  </v-alert>
                </v-col>
              </v-row>

              <v-subheader class="px-0">{{ $t("STOCK.TRANSFER_ORDERS.LINES") }}</v-subheader>

              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.PRODUCT_FINDER.EMPTY_LINES") }}
              </v-alert>
              <div v-else class="stock-document-lines-wrap">
                <v-simple-table dense class="stock-document-lines">
                  <thead>
                    <tr>
                      <th class="stock-document-lines__sticky stock-document-lines__sticky--index">
                        #
                      </th>
                      <th
                        class="stock-document-lines__sticky stock-document-lines__sticky--product"
                      >
                        {{ $t("STOCK.PRODUCTS.PRODUCT") }}
                      </th>
                      <th class="stock-document-lines__col-qty">
                        {{ $t("STOCK.TRANSFER_ORDERS.REQUESTED_QTY") }}
                      </th>
                      <th
                        v-if="showShipColumns"
                        class="stock-document-lines__col-lot"
                      >
                        {{ $t("STOCK.TRANSFER_ORDERS.LOT") }}
                      </th>
                      <th
                        v-if="showShipColumns"
                        class="stock-document-lines__col-qty"
                      >
                        {{ $t("STOCK.TRANSFER_ORDERS.SHIPPED_QTY") }}
                      </th>
                      <th
                        v-if="showReceiveColumns"
                        class="stock-document-lines__col-qty"
                      >
                        {{ $t("STOCK.TRANSFER_ORDERS.RECEIVED_QTY") }}
                      </th>
                      <th
                        v-if="showVarianceColumns"
                        class="stock-document-lines__col-variance"
                      >
                        {{ $t("STOCK.TRANSFER_ORDERS.VARIANCE_ALLOCATION") }}
                      </th>
                      <th v-if="canEditLineStructure"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(line, index) in lines"
                      :key="`line-${index}-${line.stock_product_id}`"
                      :class="{
                        'stock-document-lines__row--lot-error':
                          showShipEdit &&
                          lineExceedsLotAvailable(line, 'shipped_quantity'),
                        'stock-document-lines__row--variance':
                          line.has_variance || lineHasQtyVariance(line),
                      }"
                    >
                      <td
                        class="stock-document-lines__sticky stock-document-lines__sticky--index"
                      >
                        {{ index + 1 }}
                      </td>
                      <td
                        class="stock-document-lines__sticky stock-document-lines__sticky--product stock-document-lines__product"
                      >
                        <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                        <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                      </td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineField
                          v-if="canSave"
                          v-model.number="line.requested_quantity"
                          type="number"
                          :hint="lineUnitCode(line)"
                          @focus="focusSelectAllInput"
                        />
                        <span v-else>
                          {{ formatQty(line.requested_quantity) }}
                          <span class="caption grey--text">{{ lineUnitCode(line) }}</span>
                        </span>
                      </td>
                      <td v-if="showShipColumns" class="stock-document-lines__col-lot">
                        <v-autocomplete
                          v-if="showShipEdit"
                          v-model="line.stock_lot_id"
                          :items="lotOptionsForLine(line)"
                          item-text="label"
                          item-value="id"
                          dense
                          hide-details
                          clearable
                          :error="lineExceedsLotAvailable(line, 'shipped_quantity')"
                          :placeholder="$t('STOCK.TRANSFER_ORDERS.LOT_PLACEHOLDER')"
                          @change="onLotSelected(line, $event)"
                        ></v-autocomplete>
                        <span v-else>{{ lineLotDisplay(line) }}</span>
                      </td>
                      <td v-if="showShipColumns" class="stock-document-lines__col-qty">
                        <StockLineField
                          v-if="showShipEdit"
                          v-model.number="line.shipped_quantity"
                          type="number"
                          :hint="
                            line.stock_lot_id
                              ? lineLotQuantityHint(line, 'shipped_quantity', lineUnitCode(line))
                              : lineUnitCode(line)
                          "
                          :error="lineExceedsLotAvailable(line, 'shipped_quantity')"
                          @focus="focusSelectAllInput"
                        />
                        <span v-else>
                          {{ formatQty(line.shipped_quantity) }}
                        </span>
                      </td>
                      <td v-if="showReceiveColumns" class="stock-document-lines__col-qty">
                        <StockLineField
                          v-if="showReceiveEdit"
                          v-model.number="line.received_quantity"
                          type="number"
                          :hint="lineUnitCode(line)"
                          @focus="focusSelectAllInput"
                        />
                        <span v-else>
                          {{ formatQty(line.received_quantity) }}
                        </span>
                      </td>
                      <td v-if="showVarianceColumns" class="stock-document-lines__col-variance">
                        <div
                          v-if="
                            canResolveVariance &&
                            isLineVariancePending(line) &&
                            (line.has_variance || lineHasQtyVariance(line))
                          "
                          class="d-flex align-center"
                        >
                          <StockLineField
                            v-model.number="line.variance_qty_to_sender"
                            type="number"
                            narrow
                            dense
                            :hint="$t('STOCK.TRANSFER_ORDERS.VARIANCE_QTY_TO_SENDER')"
                            @focus="focusSelectAllInput"
                          />
                          <span class="mx-1 caption grey--text">/</span>
                          <StockLineField
                            v-model.number="line.variance_qty_to_receiver"
                            type="number"
                            narrow
                            dense
                            :hint="$t('STOCK.TRANSFER_ORDERS.VARIANCE_QTY_TO_RECEIVER')"
                            @focus="focusSelectAllInput"
                          />
                        </div>

                        <span
                          v-else-if="
                            line.variance_qty_to_sender != null ||
                            line.variance_qty_to_receiver != null
                          "
                        >
                          {{ formatQty(line.variance_qty_to_sender) }} /
                          {{ formatQty(line.variance_qty_to_receiver) }}
                        </span>

                        <span v-else>-</span>
                      </td>
                      <td v-if="canEditLineStructure" class="stock-document-lines__actions text-nowrap">
                        <v-btn
                          v-if="line.stock_product_id"
                          icon
                          small
                          :title="$t('STOCK.TRANSFER_ORDERS.DUPLICATE_PRODUCT_LINE')"
                          @click="duplicateProductLine(index)"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn icon small @click="removeLine(index)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <StockProductLineFinder
                :disabled="!canEditLineStructure"
                @add="addProductFromFinder"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockLotQuantityValidationMixin from "@/views/partials/stock/stockLotQuantityValidation.mixin";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_STOCK_TRANSFER_ORDER,
  CREATE_STOCK_TRANSFER_ORDER,
  UPDATE_STOCK_TRANSFER_ORDER,
  CONFIRM_STOCK_TRANSFER_ORDER,
  SHIP_STOCK_TRANSFER_ORDER,
  RECEIVE_STOCK_TRANSFER_ORDER,
  RESOLVE_STOCK_TRANSFER_ORDER_VARIANCE,
  CANCEL_STOCK_TRANSFER_ORDER,
  GET_STOCK_TRANSFER_ORDER_SUGGEST_DRAFT_LOTS,
  GET_STOCK_TRANSFER_ORDER_ACTIONABLE_SUMMARY,
} from "@/store/stock/stockTransferOrders.module";

const emptyLine = () => ({
  stock_product_id: null,
  requested_quantity: 1,
  shipped_quantity: null,
  received_quantity: null,
  unit_id: null,
  unit_code: null,
  lot_number: "",
  stock_lot_id: null,
  variance_resolution: null,
  variance_qty_to_sender: null,
  variance_qty_to_receiver: null,
  has_variance: false,
  notes: null,
});

export default {
  name: "StockViewTransferOrder",
  components: {
    StockProductLineFinder,
    StockLineField,
    StockDocumentPageHeading,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentContextMixin,
    stockLotQuantityValidationMixin,
  ],
  data() {
    return {
      lineQuantityKey: "requested_quantity",
      tab: "main",
      isProcessing: false,
      document: this.emptyDocument(),
      lines: [],
      lotSuggestions: [],
      lotSuggestionsReady: false,
      organizations: [],
      sourceWarehouseOptions: [],
      destinationWarehouseOptionsAll: [],
      validationErrors: {},
    };
  },
  computed: {
    orderId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    isNew() {
      return !this.$route.params.id;
    },
    canSave() {
      if (this.isReadOnlyDocument) {
        return false;
      }
      return this.isNew
        ? this.hasPermissions("stock-transfer-orders-create")
        : this.hasPermissions("stock-transfer-orders-edit") &&
            this.document.status === "draft";
    },
    canEditLineStructure() {
      return this.canSave;
    },
    canConfirm() {
      return (
        !this.isNew &&
        this.document.status === "draft" &&
        this.hasPermissions("stock-transfer-orders-confirm")
      );
    },
    canShip() {
      return (
        !this.isNew &&
        this.document.status === "confirmed" &&
        this.hasPermissions("stock-transfer-orders-ship")
      );
    },
    canReceive() {
      return (
        !this.isNew &&
        this.document.status === "shipped" &&
        this.hasPermissions("stock-transfer-orders-receive")
      );
    },
    canResolveVariance() {
      return (
        !this.isNew &&
        this.document.status === "received" &&
        this.hasPendingVariance &&
        this.hasPermissions("stock-transfer-orders-receive")
      );
    },
    canCancel() {
      return (
        !this.isNew &&
        ["draft", "confirmed"].includes(this.document.status) &&
        this.hasPermissions("stock-transfer-orders-cancel")
      );
    },
    canDiscard() {
      return this.isNew;
    },
    isReadOnlyDocument() {
      if (this.isNew) {
        return false;
      }
      return this.document.status !== "draft";
    },
    showShipColumns() {
      return ["confirmed", "shipped", "received", "closed"].includes(this.document.status);
    },
    showShipEdit() {
      return this.canShip;
    },
    showReceiveColumns() {
      return ["shipped", "received", "closed"].includes(this.document.status);
    },
    showReceiveEdit() {
      return this.canReceive;
    },
    showVarianceColumns() {
      return (
        this.showReceiveColumns ||
        this.document.status === "received" ||
        this.document.status === "closed"
      );
    },
    showVarianceEdit() {
      return this.canReceive || this.canResolveVariance;
    },
    hasPendingVariance() {
      return (this.lines || []).some(
        (line) =>
          (line.has_variance || this.lineHasQtyVariance(line)) &&
          (!line.variance_resolution || line.variance_resolution === "pending")
      );
    },
    isLotFreeStockValidationEnabled() {
      return this.showShipEdit;
    },
    lotSuggestionSignature() {
      const productIds = [
        ...new Set(
          (this.lines || [])
            .map((line) => line.stock_product_id)
            .filter(Boolean)
            .map(Number)
        ),
      ].sort((a, b) => a - b);
      return `${this.document.source_warehouse_id || ""}|${productIds.join(",")}|${
        this.document.status || ""
      }`;
    },
    destinationWarehouseOptions() {
      const sourceId = Number(this.document.source_warehouse_id || 0);
      return (this.destinationWarehouseOptionsAll || []).filter(
        (warehouse) => Number(warehouse.id) !== sourceId
      );
    },
    statusColor() {
      const map = {
        draft: "grey",
        confirmed: "blue",
        shipped: "orange",
        received: "teal",
        closed: "success",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.TRANSFER_ORDER.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
  },
  watch: {
    "$route.params.id"(id, previousId) {
      if (!id || id === previousId) {
        return;
      }
      if (String(this.document.id) === String(id)) {
        return;
      }
      this.loadOrder(id);
    },
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
    },
    lotSuggestionSignature() {
      this.lotSuggestionsReady = false;
      this.queueLotSuggestionsLoad();
    },
  },
  created() {
    this.queueLotSuggestionsLoad = debounce(() => {
      this.loadLotSuggestions();
    }, 300);
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    emptyDocument() {
      return {
        organization_id: null,
        source_warehouse_id: null,
        destination_warehouse_id: null,
        requested_date: new Date().toISOString().slice(0, 10),
        notes: null,
        status: "draft",
        code: null,
        shipped_action: null,
        received_action: null,
      };
    },
    computeLineNetFromUnit() {
      return 0;
    },
    formatQty(value) {
      if (value == null || value === "") {
        return "-";
      }
      return Number(value);
    },
    lineHasQtyVariance(line) {
      if (line.shipped_quantity == null || line.received_quantity == null) {
        return false;
      }
      return (
        Math.abs(Number(line.shipped_quantity) - Number(line.received_quantity)) > 0.0001
      );
    },
    isLineVariancePending(line) {
      return (
        (line.has_variance || this.lineHasQtyVariance(line)) &&
        (!line.variance_resolution || line.variance_resolution === "pending")
      );
    },
    lineLotDisplay(line) {
      if (line.stock_lot && line.stock_lot.lot_number) {
        return line.stock_lot.lot_number;
      }
      return line.lot_number || "-";
    },
    validateOrganizationAndWarehouseRequired() {
      let valid = true;
      const errors = { ...(this.validationErrors || {}) };

      if (!this.document.organization_id) {
        errors.organization_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.organization_id;
      }

      if (!this.document.source_warehouse_id) {
        errors.source_warehouse_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.source_warehouse_id;
      }

      if (!this.document.destination_warehouse_id) {
        errors.destination_warehouse_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.destination_warehouse_id;
      }

      if (
        this.document.source_warehouse_id &&
        this.document.destination_warehouse_id &&
        Number(this.document.source_warehouse_id) ===
          Number(this.document.destination_warehouse_id)
      ) {
        errors.destination_warehouse_id = [
          this.$t("STOCK.TRANSFER_ORDERS.WAREHOUSES_MUST_DIFFER"),
        ];
        valid = false;
      }

      this.validationErrors = errors;

      if (!valid) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.ORG_AND_WAREHOUSES_REQUIRED_SAVE"),
          color: "error",
        });
      }

      return valid;
    },
    organizationIdsForWarehouseQuery() {
      const docOrgId = this.document.organization_id
        ? Number(this.document.organization_id)
        : null;

      if (docOrgId) {
        return [docOrgId];
      }

      const contextOrgIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      if (contextOrgIds.length > 0) {
        return contextOrgIds;
      }

      return [];
    },
    async loadOrganizations() {
      const contextIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      const params = {};
      if (contextIds.length > 0) {
        params.organization_ids = contextIds;
      }

      const orgs = await this.$store.dispatch(GET_ORGANIZATIONS_OPTIONS, params);
      let list = orgs.data || orgs;

      if (contextIds.length > 0) {
        list = list.filter((org) => contextIds.includes(Number(org.id)));
      }

      this.organizations = list;
    },
    async loadWarehouses() {
      const organizationIds = this.organizationIdsForWarehouseQuery();
      const contextWarehouseIds = (this.$store.getters.stockContextWarehouseIds || []).map(
        Number
      );
      const selectedSourceId = this.document.source_warehouse_id
        ? Number(this.document.source_warehouse_id)
        : null;
      const selectedDestinationId = this.document.destination_warehouse_id
        ? Number(this.document.destination_warehouse_id)
        : null;

      const sourceParams = {
        organization_ids: organizationIds,
        all_organization_warehouses: 1,
      };
      const destinationParams = {
        organization_ids: organizationIds,
      };
      if (contextWarehouseIds.length > 0) {
        destinationParams.warehouse_ids = [
          ...new Set([
            ...contextWarehouseIds,
            ...(selectedDestinationId ? [selectedDestinationId] : []),
          ]),
        ];
      }

      const [sourceData, destinationData] = await Promise.all([
        this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, sourceParams),
        this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, destinationParams),
      ]);

      const sourceWarehouses = sourceData.data || sourceData;
      const destinationWarehouses = destinationData.data || destinationData;

      this.sourceWarehouseOptions = this.mapWarehouseOptions(sourceWarehouses, [
        "physical",
        "virtual",
      ]);
      let destinationOptions = this.mapWarehouseOptions(destinationWarehouses, [
        "physical",
        "virtual",
      ]);

      if (contextWarehouseIds.length > 0) {
        destinationOptions = destinationOptions.filter(
          (warehouse) =>
            contextWarehouseIds.includes(Number(warehouse.id)) ||
            Number(warehouse.id) === selectedDestinationId ||
            warehouse.type === "virtual"
        );
      }

      this.destinationWarehouseOptionsAll = destinationOptions;

      if (selectedSourceId) {
        const selected = sourceWarehouses.find(
          (warehouse) => Number(warehouse.id) === selectedSourceId
        );
        if (selected) {
          this.sourceWarehouseOptions = this.mergeOptionIntoList(
            this.sourceWarehouseOptions,
            {
              ...selected,
              label: selected.label || this.warehouseOptionLabel(selected),
            }
          );
        }
      }

      if (selectedDestinationId) {
        const selected = destinationWarehouses.find(
          (warehouse) => Number(warehouse.id) === selectedDestinationId
        );
        if (selected) {
          this.destinationWarehouseOptionsAll = this.mergeOptionIntoList(
            this.destinationWarehouseOptionsAll,
            {
              ...selected,
              label: selected.label || this.warehouseOptionLabel(selected),
            }
          );
        }
      }

      if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
        this.reconcileHeaderOptionsFromSeed();
      }
    },
    resetDocumentSelectionsOutsideContext() {
      if (this.suppressStockHeaderReload) {
        return;
      }

      const orgId = this.document.organization_id;
      if (
        orgId &&
        !this.organizations.some((org) => Number(org.id) === Number(orgId))
      ) {
        if (!(this.isNew && this.headerOptionsSeed)) {
          this.document.organization_id = null;
          this.document.source_warehouse_id = null;
          this.document.destination_warehouse_id = null;
        }
        return;
      }

      const sourceId = this.document.source_warehouse_id;
      if (
        sourceId &&
        !this.sourceWarehouseOptions.some(
          (warehouse) => Number(warehouse.id) === Number(sourceId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.sourceWarehouseOptions.some(
            (warehouse) => Number(warehouse.id) === Number(sourceId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.source_warehouse_id = null;
        }
      }

      const destinationId = this.document.destination_warehouse_id;
      if (
        destinationId &&
        !this.destinationWarehouseOptionsAll.some(
          (warehouse) => Number(warehouse.id) === Number(destinationId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.destinationWarehouseOptionsAll.some(
            (warehouse) => Number(warehouse.id) === Number(destinationId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.destination_warehouse_id = null;
        }
      }
    },
    applyStockContextDefaultsOnNewDocument() {
      if (!this.isNew) {
        return;
      }

      if (!this.document.organization_id && this.organizations.length === 1) {
        this.document.organization_id = this.organizations[0].id;
      }

      if (
        !this.document.destination_warehouse_id &&
        this.destinationWarehouseOptionsAll.length === 1
      ) {
        this.document.destination_warehouse_id = this.destinationWarehouseOptionsAll[0].id;
      }

      this.applyRouteQueryDefaultsOnNewDocument();
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await this.loadOrganizations();
        await this.loadWarehouses();
        if (!this.isNew) {
          await this.loadOrder(this.$route.params.id);
        } else {
          await this.withSuppressedStockHeaderReload(async () => {
            this.applyStockContextDefaultsOnNewDocument();
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadOrder(id) {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(GET_STOCK_TRANSFER_ORDER, id);
        await this.applyDocument(data);
        await this.loadLotSuggestions();
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadLotSuggestions() {
      this.lotSuggestionsReady = false;

      try {
        if (!this.showShipEdit && this.document.status !== "confirmed") {
          this.lotSuggestions = [];
          return;
        }

        const warehouseId = this.document.source_warehouse_id;
        const productIds = [
          ...new Set(
            (this.lines || [])
              .map((line) => line.stock_product_id)
              .filter(Boolean)
              .map(Number)
          ),
        ];

        if (!warehouseId || !productIds.length) {
          this.lotSuggestions = [];
          return;
        }

        const data = await this.$store.dispatch(GET_STOCK_TRANSFER_ORDER_SUGGEST_DRAFT_LOTS, {
          params: {
            source_warehouse_id: warehouseId,
            stock_product_ids: productIds,
            as_of_date: this.document.requested_date,
          },
        });
        this.lotSuggestions = Array.isArray(data) ? data : data.lots || data.items || [];
      } catch (e) {
        this.lotSuggestions = [];
      } finally {
        this.lotSuggestionsReady = true;
      }
    },
    lotOptionsForLine(line) {
      if (!line.stock_product_id) {
        return [];
      }

      const options = (this.lotSuggestions || [])
        .filter((row) => Number(row.stock_product_id) === Number(line.stock_product_id))
        .map((row) => ({
          id: row.stock_lot_id || row.id,
          label: row.label || row.lot_number || String(row.stock_lot_id || row.id),
          lot_number: row.lot_number,
          free_quantity: row.free_quantity != null ? Number(row.free_quantity) : null,
        }));

      return this.mergeSelectedLotOption(line, options);
    },
    onLotSelected(line, lotId) {
      if (!lotId) {
        line.lot_number = "";
        return;
      }
      const option = this.lotOptionsForLine(line).find(
        (item) => Number(item.id) === Number(lotId)
      );
      if (option) {
        line.lot_number = option.lot_number || option.label || "";
      }
    },
    mapTransferOrderDocument(data) {
      return {
        organization_id: data.organization_id ?? data.organization?.id ?? null,
        source_warehouse_id: data.source_warehouse_id ?? data.source_warehouse?.id ?? null,
        destination_warehouse_id:
          data.destination_warehouse_id ?? data.destination_warehouse?.id ?? null,
        shipped_action: data.shipped_action || null,
        received_action: data.received_action || null,
      };
    },
    syncTransferOrderHeaderOptions(data) {
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code || String(data.organization.id),
          ...data.organization,
        });
      }
      if (data.source_warehouse) {
        const wh = {
          ...data.source_warehouse,
          label: this.warehouseOptionLabel(data.source_warehouse),
        };
        this.sourceWarehouseOptions = this.mergeOptionIntoList(
          this.sourceWarehouseOptions,
          wh
        );
      }
      if (data.destination_warehouse) {
        const wh = {
          ...data.destination_warehouse,
          label: this.warehouseOptionLabel(data.destination_warehouse),
        };
        this.destinationWarehouseOptionsAll = this.mergeOptionIntoList(
          this.destinationWarehouseOptionsAll,
          wh
        );
      }
    },
    async applyDocument(data) {
      await this.withSuppressedStockHeaderReload(async () => {
        this.document = {
          ...this.emptyDocument(),
          ...data,
          ...this.mapTransferOrderDocument(data),
        };
        await this.loadWarehouses();
        if (this.isNew) {
          this.setHeaderOptionsSeed(data, "syncTransferOrderHeaderOptions");
        } else {
          this.clearHeaderOptionsSeed();
        }
        this.syncTransferOrderHeaderOptions(data);
      });
      this.lines = (data.items || []).map((item) => ({
        id: item.id,
        stock_product_id: item.stock_product_id,
        stock_product: item.stock_product || null,
        requested_quantity: Number(item.requested_quantity || 0),
        shipped_quantity:
          item.shipped_quantity != null
            ? Number(item.shipped_quantity)
            : Number(item.requested_quantity || 0),
        received_quantity:
          item.received_quantity != null
            ? Number(item.received_quantity)
            : item.shipped_quantity != null
              ? Number(item.shipped_quantity)
              : null,
        ...this.mapLineUnitFields(item),
        lot_number: item.lot_number || item.stock_lot?.lot_number || "",
        stock_lot_id: item.stock_lot_id || item.stock_lot?.id || null,
        stock_lot: item.stock_lot || null,
        variance_resolution: item.variance_resolution || null,
        variance_qty_to_sender:
          item.variance_qty_to_sender != null
            ? Number(item.variance_qty_to_sender)
            : null,
        variance_qty_to_receiver:
          item.variance_qty_to_receiver != null
            ? Number(item.variance_qty_to_receiver)
            : null,
        has_variance: !!item.has_variance,
        notes: item.notes,
      }));
    },
    payload() {
      return {
        organization_id: this.document.organization_id,
        source_warehouse_id: this.document.source_warehouse_id,
        destination_warehouse_id: this.document.destination_warehouse_id,
        requested_date: this.document.requested_date,
        notes: this.document.notes,
        items: this.lines
          .filter((l) => l.stock_product_id)
          .map((line, index) => ({
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            requested_quantity: line.requested_quantity,
            unit_id: line.unit_id,
            notes: line.notes,
          })),
      };
    },
    shipPayload() {
      return {
        items: this.lines
          .filter((l) => l.id)
          .map((line) => ({
            id: line.id,
            stock_lot_id: line.stock_lot_id,
            shipped_quantity: line.shipped_quantity,
          })),
      };
    },
    receivePayload() {
      return {
        items: this.lines
          .filter((l) => l.id)
          .map((line) => {
            const row = {
              id: line.id,
              received_quantity: line.received_quantity,
            };
            if (this.lineHasQtyVariance(line) || line.has_variance) {
              row.variance_resolution = line.variance_resolution || "pending";
            }
            return row;
          }),
      };
    },
    resolveVariancePayload() {
      return {
        items: this.lines
          .filter(
            (l) =>
              l.id &&
              (l.has_variance || this.lineHasQtyVariance(l)) &&
              this.isLineVariancePending(l)
          )
          .map((line) => ({
            id: line.id,
            variance_qty_to_sender: line.variance_qty_to_sender,
            variance_qty_to_receiver: line.variance_qty_to_receiver,
          })),
      };
    },
    async save() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_TRANSFER_ORDER, params);
          this.$router.replace({
            name: "stock-transfer-orders-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_TRANSFER_ORDER, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async confirmOrder() {
      if (this.canSave) {
        await this.save();
        if (this.isNew || this.document.status !== "draft") {
          return;
        }
      }
      if (!confirm(this.$t("STOCK.TRANSFER_ORDERS.CONFIRM_CONFIRM"))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          CONFIRM_STOCK_TRANSFER_ORDER,
          this.$route.params.id
        );
        await this.applyDocument(data);
        await this.loadLotSuggestions();
        this.refreshActionableSummary();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.CONFIRMED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async shipOrder() {
      const missingLot = this.lines.some((line) => line.id && !line.stock_lot_id);
      if (missingLot) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.LOT_REQUIRED_SHIP"),
          color: "error",
        });
        return;
      }
      if (!this.validateLotQuantitiesOrNotify("shipped_quantity")) {
        return;
      }
      if (!confirm(this.$t("STOCK.TRANSFER_ORDERS.CONFIRM_SHIP"))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(SHIP_STOCK_TRANSFER_ORDER, {
          id: this.$route.params.id,
          params: this.shipPayload(),
        });
        await this.applyDocument(data);
        this.refreshActionableSummary();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.SHIPPED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async receiveOrder() {
      if (!confirm(this.$t("STOCK.TRANSFER_ORDERS.CONFIRM_RECEIVE"))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(RECEIVE_STOCK_TRANSFER_ORDER, {
          id: this.$route.params.id,
          params: this.receivePayload(),
        });
        await this.applyDocument(data);
        this.refreshActionableSummary();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.RECEIVED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async resolveVariance() {
      const pendingLines = this.lines.filter(
        (line) =>
          line.id &&
          (line.has_variance || this.lineHasQtyVariance(line)) &&
          this.isLineVariancePending(line)
      );

      if (pendingLines.length === 0) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.VARIANCE_MUST_RESOLVE"),
          color: "error",
        });
        return;
      }

      const hasMissingAllocations = pendingLines.some(
        (line) =>
          line.variance_qty_to_sender == null || line.variance_qty_to_receiver == null
      );

      if (hasMissingAllocations) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.VARIANCE_MUST_RESOLVE"),
          color: "error",
        });
        return;
      }

      const hasInvalidSums = pendingLines.some((line) => {
        const shippedQty = Number(line.shipped_quantity || 0);
        const receivedQty = Number(line.received_quantity || 0);
        const varianceQty = Math.abs(shippedQty - receivedQty);
        const toSender = Number(line.variance_qty_to_sender || 0);
        const toReceiver = Number(line.variance_qty_to_receiver || 0);
        return Math.abs(toSender + toReceiver - varianceQty) > 0.0001;
      });

      if (hasInvalidSums) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t(
            "STOCK.TRANSFER_ORDERS.VARIANCE_ALLOCATION_SUM_MUST_MATCH"
          ),
          color: "error",
        });
        return;
      }

      if (!confirm(this.$t("STOCK.TRANSFER_ORDERS.CONFIRM_RESOLVE_VARIANCE"))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(RESOLVE_STOCK_TRANSFER_ORDER_VARIANCE, {
          id: this.$route.params.id,
          params: this.resolveVariancePayload(),
        });
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.VARIANCE_RESOLVED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async cancelOrder() {
      if (!confirm(this.$t("STOCK.TRANSFER_ORDERS.CONFIRM_CANCEL"))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          CANCEL_STOCK_TRANSFER_ORDER,
          this.$route.params.id
        );
        await this.applyDocument(data);
        this.refreshActionableSummary();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSFER_ORDERS.CANCELLED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    refreshActionableSummary() {
      this.$store.dispatch(GET_STOCK_TRANSFER_ORDER_ACTIONABLE_SUMMARY).catch(() => {});
    },
    discardOrder() {
      this.$router.push({ name: "stock-transfer-orders" });
    },
    addProductFromFinder(product) {
      this.addProductLine(product, emptyLine);
    },
    duplicateProductLine(index) {
      const line = this.lines[index];
      if (!line?.stock_product_id || !line.stock_product) {
        return;
      }
      this.insertProductLineAfter(line.stock_product, emptyLine, index);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 44px;
  z-index: 2;
  min-width: 260px;
  width: 260px;
  max-width: 280px;
  background: #fff;
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.14);
}
.stock-document-lines thead .stock-document-lines__sticky--index,
.stock-document-lines thead .stock-document-lines__sticky--product {
  background: #f5f5f5;
  z-index: 3;
}
.stock-document-lines__col-qty {
  min-width: 100px;
  width: 110px;
}
.stock-document-lines__col-lot {
  min-width: 160px;
  width: 180px;
}
.stock-document-lines__col-variance {
  min-width: 160px;
  width: 180px;
}
.stock-document-lines__row--lot-error {
  background: rgba(244, 67, 54, 0.06);
}
.stock-document-lines__row--variance {
  background: rgba(255, 152, 0, 0.08);
}
</style>
